<div class="container">
  <section class="users-section">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="section-title">
        Invite Users
        @if (inviteMode() === 'project') {
          <span class="text-muted small fw-normal ms-2">— {{ activeProjectLabel() }}</span>
        }
      </h2>
      <div class="d-flex align-items-center gap-2">
        <button
          class="btn"
          type="button"
          (click)="openProjectInvite()"
          [disabled]="inviting()"
          [class.btn-primary]="inviteMode() === 'project'"
          [class.btn-outline-secondary]="inviteMode() !== 'project'"
        >
          Invite to Project
        </button>
        <button
          class="btn"
          type="button"
          (click)="openOrganizationInvite()"
          [disabled]="inviting()"
          [class.btn-primary]="inviteMode() === 'organization'"
          [class.btn-outline-secondary]="inviteMode() !== 'organization'"
        >
          Invite to Organization
        </button>
      </div>
    </div>
    <hr />

    <div class="row g-3 mb-3">
      <div class="col-12">
        <div class="filter-card invite-card p-3 rounded-4 shadow-sm bg-white">
          <div class="fw-500 mb-2">Invite a user</div>
          <div class="row g-2">
            <div class="col-md-4">
              <label class="form-label">Target email</label>
              <div class="input-group">
                <input
                  class="form-control"
                  [class.is-valid]="emailLookup().status === 'found'"
                  [class.is-invalid]="emailLookup().status === 'not_found' || emailLookup().status === 'invalid' || emailLookup().status === 'error'"
                  [value]="inviteToEmail()"
                  (input)="inviteToEmail.set($any($event.target).value)"
                  placeholder="name@company.com"
                  autocomplete="off"
                />
                <span class="input-group-text bg-transparent">
                  @if (emailLookup().status === 'checking') {
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  } @else if (emailLookup().status === 'found') {
                    <i class="bi bi-check-circle-fill text-success"></i>
                  } @else if (emailLookup().status === 'not_found' || emailLookup().status === 'invalid' || emailLookup().status === 'error') {
                    <i class="bi bi-x-circle-fill text-danger"></i>
                  } @else {
                    <i class="bi bi-envelope text-muted"></i>
                  }
                </span>
              </div>
              @if (emailLookup().status === 'not_found' || emailLookup().status === 'invalid' || emailLookup().status === 'error') {
                <div class="invalid-feedback d-block">{{ emailLookup().message || 'Invalid email' }}</div>
              }
            </div>
            <div class="col-md-4">
              <label class="form-label">Project role</label>
              <select class="form-select" [value]="inviteProjectRole()" (change)="inviteProjectRole.set($any($event.target).value)">
                @for (r of roles; track r) {
                  <option [value]="r">{{ r }}</option>
                }
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Organization role</label>
              <select class="form-select" [value]="inviteOrgRole()" (change)="inviteOrgRole.set($any($event.target).value)">
                @for (r of orgRoles; track r) {
                  <option [value]="r">{{ r }}</option>
                }
              </select>
            </div>
            <div class="col-12">
              <!-- Intentionally blank: removed helper text -->
            </div>

            @if (inviteMode() === 'project') {
              <div class="col-12 d-flex gap-2">
                <button
                  class="btn btn-sm btn-primary"
                  type="button"
                  (click)="inviteToProject()"
                  [disabled]="inviting() || emailLookup().status !== 'found'"
                >
                  @if (!inviting()) { Send project invite } @else { Sending... }
                </button>
              </div>
            }

            @if (inviteMode() === 'organization') {
              <div class="col-md-6">
                <label class="form-label">Organization</label>
                <select class="form-select" [value]="bulkOrgId()" (change)="onBulkOrgChange($any($event.target).value)">
                  <option value="" disabled>Select an organization</option>
                  @for (o of organizations(); track o.id) {
                    <option [value]="o.id">{{ o.name }}</option>
                  }
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Project role (applied to selected projects)</label>
                <select class="form-select" [value]="bulkProjectRole()" (change)="bulkProjectRole.set($any($event.target).value)">
                  @for (r of roles; track r) {
                    <option [value]="r">{{ r }}</option>
                  }
                </select>
              </div>
              <div class="col-12">
                <div class="d-flex align-items-center justify-content-between mt-3">
                  <div class="fw-500">Projects in organization</div>
                  <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-outline-secondary" type="button" (click)="selectAllBulkProjects()" [disabled]="bulkProjects().length===0">Select all</button>
                    <button class="btn btn-sm btn-outline-secondary" type="button" (click)="clearBulkProjects()" [disabled]="bulkSelectedProjectIds().length===0">Clear</button>
                  </div>
                </div>
                @if (bulkLoadingProjects()) {
                  <div class="text-muted small mt-2">Loading projects…</div>
                } @else {
                  @if (bulkProjects().length === 0) {
                    <div class="text-muted small mt-2">No projects found for this organization.</div>
                  } @else {
                    <div class="d-flex flex-wrap gap-2 mt-2">
                      @for (p of bulkProjects(); track p.id) {
                        <button
                          type="button"
                          class="btn btn-sm"
                          [class.btn-primary]="bulkSelectedProjectIds().includes(p.id)"
                          [class.btn-outline-secondary]="!bulkSelectedProjectIds().includes(p.id)"
                          (click)="toggleBulkProject(p.id)"
                        >
                          {{ p.name || p.id }}
                        </button>
                      }
                    </div>
                  }
                }
              </div>
              <div class="col-12 d-flex gap-2 mt-4">
                <button
                  class="btn btn-sm btn-primary"
                  type="button"
                  (click)="sendBulkOrganizationInvite()"
                  [disabled]="inviting() || emailLookup().status !== 'found'"
                >
                  @if (!inviting()) { Send bulk org invite } @else { Sending... }
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="users-section">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="section-title">
        Organization and Project Users
        <span class="text-muted small fw-normal ms-2">— {{ activeOrganizationLabel() }}</span>
      </h2>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" (click)="resetFilters()">Reset</button>
        <button class="btn btn-primary" (click)="refresh()" [disabled]="loading()">Refresh</button>
      </div>
    </div>
    <hr />

    <div class="row g-3">
      <div class="col-md-4">
        <div class="filter-card p-3 rounded-4 shadow-sm bg-white">
          <div class="mb-2 fw-500">Filters</div>
          <div class="mb-2">
            <label class="form-label">Search</label>
            <input type="text" class="form-control" placeholder="Email / role" [value]="search()" (input)="onSearchChange($any($event.target).value)" />
          </div>
          <div class="mb-2">
            <label class="form-label">Organization role</label>
            <select class="form-select" [value]="orgRoleFilter()" (change)="onOrgRoleFilterChange($any($event.target).value)">
              <option value="">All</option>
              @for (r of orgRoles; track r) {
                <option [value]="r">{{ r }}</option>
              }
            </select>
          </div>
          <div class="mb-2">
            <label class="form-label">Project role</label>
            <select class="form-select" [value]="projectRoleFilter()" (change)="onProjectRoleFilterChange($any($event.target).value)">
              <option value="">All</option>
              @for (r of roles; track r) {
                <option [value]="r">{{ r }}</option>
              }
            </select>
          </div>
        </div>
      </div>

      <div class="col-md-8">
        <div class="d-flex flex-column gap-3">
          <div class="table-card p-3 rounded-4 shadow-sm bg-white">
            <div class="fw-500 mb-2">Organization users - {{ activeOrganizationLabel() }}</div>
            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">User</th>
                    <th scope="col">Role</th>
                    <th scope="col" class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @if (loading()) {
                    <tr>
                      <td colspan="4" class="text-muted small">Loading…</td>
                    </tr>
                  } @else {
                    @for (u of filteredOrganizationUsers(); track u.id) {
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-3">
                            <img class="avatar" src="/profile-placeholder.png" alt="avatar" />
                            <div>
                              <div class="fw-500">
                                {{ userEmail(u.userId) || '—' }}
                                @if (u.userId === currentUserId()) {
                                  <span class="badge bg-secondary ms-2">You</span>
                                }
                              </div>
                              <div class="text-muted small">Organization member</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <select
                            class="form-select form-select-sm w-auto"
                            [value]="$any(u).role"
                            [disabled]="u.userId === currentUserId()"
                            (change)="updateOrgRole(u, $any($event.target).value)"
                          >
                            @for (r of orgRoles; track r) {
                              <option [value]="r">{{ r }}</option>
                            }
                          </select>
                        </td>
                        <td class="text-end">
                          <button
                            class="btn btn-sm btn-outline-danger"
                            [disabled]="u.userId === currentUserId()"
                            (click)="removeFromOrg(u)"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    }
                    @if (filteredOrganizationUsers().length === 0) {
                      <tr>
                        <td colspan="3" class="text-muted small">No organization users found.</td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          </div>

          <div class="table-card p-3 rounded-4 shadow-sm bg-white">
            <div class="fw-500 mb-2">Project users - {{ activeProjectLabel() }}</div>
            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th scope="col">User</th>
                    <th scope="col">Role</th>
                    <th scope="col" class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @if (loading()) {
                    <tr>
                      <td colspan="3" class="text-muted small">Loading…</td>
                    </tr>
                  } @else {
                    @for (u of filteredProjectUsers(); track u.id) {
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-3">
                            <img class="avatar" src="/profile-placeholder.png" alt="avatar" />
                            <div>
                              <div class="fw-500">
                                {{ userEmail(u.userId) || '—' }}
                                @if (u.userId === currentUserId()) {
                                  <span class="badge bg-secondary ms-2">You</span>
                                }
                              </div>
                              <div class="text-muted small">Project member</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <select
                            class="form-select form-select-sm w-auto"
                            [value]="u.role"
                            [disabled]="u.userId === currentUserId()"
                            (change)="updateRole(u, $any($event.target).value)"
                          >
                            @for (r of roles; track r) {
                              <option [value]="r">{{ r }}</option>
                            }
                          </select>
                        </td>
                        <td class="text-end">
                          <button
                            class="btn btn-sm btn-outline-danger"
                            [disabled]="u.userId === currentUserId()"
                            (click)="openRemoveFromProject(u)"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    }
                    @if (filteredProjectUsers().length === 0) {
                      <tr>
                        <td colspan="3" class="text-muted small">No project users found.</td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="users-section">
    <div class="d-flex align-items-center justify-content-between">
      <h2 class="section-title">Outgoing invites (pending)</h2>
      <a class="btn btn-outline-secondary" routerLink="/notifications">Open Notifications</a>
    </div>
    <hr />
    <div class="table-card p-3 rounded-4 shadow-sm bg-white">
      @if (loadingInvites()) {
        <div class="text-muted small">Loading invites…</div>
      } @else {
        <div class="row g-3">
          <div class="col-12 col-lg-6">
            <div class="fw-500 mb-2">Project invites</div>
            @if (outgoingProjectInvites().length === 0) {
              <div class="text-muted small">No pending project invites.</div>
            } @else {
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>To email</th>
                      <th>Role</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (inv of outgoingProjectInvites(); track inv.id) {
                      <tr>
                        <td class="fw-500">{{ userEmail(inv.toUserId) || inv.toUserId }}</td>
                        <td>{{ inv.role }}</td>
                        <td class="text-end">
                          <button class="btn btn-sm btn-outline-danger" type="button" (click)="cancelProjectInvite(inv)">Cancel</button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>

          <div class="col-12 col-lg-6">
            <div class="fw-500 mb-2">Organization invites</div>
            @if (outgoingOrganizationInvites().length === 0) {
              <div class="text-muted small">No pending organization invites.</div>
            } @else {
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>To email</th>
                      <th>Role</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (inv of outgoingOrganizationInvites(); track inv.id) {
                      <tr>
                        <td class="fw-500">{{ userEmail(inv.toUserId) || inv.toUserId }}</td>
                        <td>{{ inv.role }}</td>
                        <td class="text-end">
                          <button class="btn btn-sm btn-outline-danger" type="button" (click)="cancelOrganizationInvite(inv)">Cancel</button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </section>

  <ng-template #confirmRemoveOrgUserModal>
    <div class="modal-header">
      <h5 class="modal-title">Remove user from organization</h5>
      <button type="button" class="btn-close" aria-label="Close" (click)="closeConfirmRemoveUserModal()" [disabled]="removingUser"></button>
    </div>
    <div class="modal-body">
      <p class="mb-2">
        You are about to remove
        <span class="fw-semibold">{{ userEmail(pendingOrgRemove?.userId) || pendingOrgRemove?.userId }}</span>
        from this organization.
      </p>
      <div class="text-muted small">
        <div><span class="fw-semibold">Organization</span>: {{ pendingOrgRemove?.organizationId }}</div>
        <div><span class="fw-semibold">Current role</span>: {{ $any(pendingOrgRemove)?.role }}</div>
      </div>
      <div class="mt-3">
        <div class="fw-semibold mb-2">Projects they will be removed from ({{ pendingOrgRemoveProjects.length }})</div>
        @if (pendingOrgRemoveProjects.length === 0) {
          <div class="text-muted small">No projects found for this organization (or still loading).</div>
        } @else {
          <div class="d-flex flex-wrap gap-2">
            @for (p of pendingOrgRemoveProjects; track p.id) {
              <span class="badge rounded-pill bg-secondary">
                {{ p.name || p.id }}
              </span>
            }
          </div>
        }
      </div>

      <div class="alert alert-warning mt-3 mb-0">
        This will also remove them from <span class="fw-semibold">all projects</span> in this organization.
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-outline-secondary" (click)="closeConfirmRemoveUserModal()" [disabled]="removingUser">Cancel</button>
      <button type="button" class="btn btn-danger" (click)="confirmRemoveFromOrganization()" [disabled]="removingUser">
        @if (removingUser) {
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        }
        {{ removingUser ? 'Removing...' : 'Remove' }}
      </button>
    </div>
  </ng-template>

  <ng-template #confirmRemoveProjectUserModal>
    <div class="modal-header">
      <h5 class="modal-title">Remove user from project</h5>
      <button type="button" class="btn-close" aria-label="Close" (click)="closeConfirmRemoveUserModal()" [disabled]="removingUser"></button>
    </div>
    <div class="modal-body">
      <p class="mb-2">
        You are about to remove
        <span class="fw-semibold">{{ userEmail(pendingProjectRemove?.userId) || pendingProjectRemove?.userId }}</span>
        from the selected project.
      </p>
      <div class="text-muted small">
        <div><span class="fw-semibold">Project</span>: {{ pendingProjectRemove?.projectId }}</div>
        <div><span class="fw-semibold">Current role</span>: {{ pendingProjectRemove?.role }}</div>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-outline-secondary" (click)="closeConfirmRemoveUserModal()" [disabled]="removingUser">Cancel</button>
      <button type="button" class="btn btn-danger" (click)="confirmRemoveFromProject()" [disabled]="removingUser">
        @if (removingUser) {
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        }
        {{ removingUser ? 'Removing...' : 'Remove' }}
      </button>
    </div>
  </ng-template>
</div>
